import React from 'react';
import './Privacy.css';

const Privacy: React.FC = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <h1 className="privacy-title">Privacy Policy</h1>
        <p className="privacy-updated">Last Updated: October 26, 2025</p>

        <div className="privacy-content">
          <section className="privacy-section">
            <h2>Introduction</h2>
            <p>
              Tempest Radio ("we," "us," or "our") respects your privacy and is committed to
              protecting your personal information. This Privacy Policy explains how we collect,
              use, disclose, and safeguard your information when you use our streaming radio
              platform and related services.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Information We Collect</h2>

            <h3>Information You Provide</h3>
            <p>When you create an account or use our services, we may collect:</p>
            <ul>
              <li>Name and username</li>
              <li>Email address</li>
              <li>Profile information (bio, profile picture)</li>
              <li>Content you upload (audio streams, metadata)</li>
              <li>Comments and interactions with other users</li>
            </ul>

            <h3>Automatically Collected Information</h3>
            <p>When you use Tempest Radio, we automatically collect:</p>
            <ul>
              <li>Device information (IP address, browser type, operating system)</li>
              <li>Usage data (streams listened to, duration, interactions)</li>
              <li>Log data (access times, pages viewed, errors encountered)</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>How We Use Your Information</h2>
            <p>We use the collected information for:</p>
            <ul>
              <li>Providing and improving our streaming services</li>
              <li>Personalizing your listening experience</li>
              <li>Communicating with you about your account and updates</li>
              <li>Analyzing usage patterns to enhance platform performance</li>
              <li>Detecting and preventing fraud, abuse, and security issues</li>
              <li>Complying with legal obligations</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Audio Content and Streaming</h2>
            <p>
              When you stream audio content on Tempest Radio, we collect listening data to
              improve recommendations and platform performance. This includes:
            </p>
            <ul>
              <li>Streams you listen to and how long you listen</li>
              <li>Playback quality and buffering events</li>
              <li>Favorites, likes, and playlist additions</li>
            </ul>
            <p>
              Content creators can see aggregated listener statistics but cannot access
              individual user identities without explicit interaction (comments, follows, etc.).
            </p>
          </section>

          <section className="privacy-section">
            <h2>Information Sharing and Disclosure</h2>
            <p>We do not sell your personal information. We may share information with:</p>

            <h3>Service Providers</h3>
            <p>
              Third-party services that help us operate our platform (hosting, analytics,
              customer support). These providers are contractually obligated to protect
              your information.
            </p>

            <h3>Content Creators</h3>
            <p>
              When you interact with streams or creators (comments, follows), that information
              becomes visible to the content creator and may be publicly displayed.
            </p>

            <h3>Legal Requirements</h3>
            <p>
              We may disclose information if required by law, court order, or to protect
              our rights, safety, or the safety of others.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar technologies to maintain your session, remember
              preferences, and analyze platform usage. You can control cookies through your
              browser settings, but some features may not function properly if disabled.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your information,
              including encryption, secure servers, and access controls. However, no method
              of transmission over the Internet is 100% secure, and we cannot guarantee
              absolute security.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Your Rights and Choices</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access and download your personal information</li>
              <li>Correct inaccurate information in your profile</li>
              <li>Delete your account and associated data</li>
              <li>Opt out of promotional communications</li>
              <li>Control cookie preferences</li>
            </ul>
            <p>
              To exercise these rights, please contact us at{' '}
              <a href="mailto:info@thetempestradio.com">info@thetempestradio.com</a>.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Children's Privacy</h2>
            <p>
              Tempest Radio is not intended for users under 13 years of age. We do not
              knowingly collect information from children under 13. If you believe we have
              collected information from a child under 13, please contact us immediately.
            </p>
          </section>

          <section className="privacy-section">
            <h2>International Users</h2>
            <p>
              Tempest Radio operates in the United States. If you access our services from
              outside the U.S., your information may be transferred to and processed in the
              United States, where data protection laws may differ from your jurisdiction.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy periodically. We will notify you of significant
              changes by posting a notice on our platform or sending an email. Your continued
              use of Tempest Radio after changes indicates your acceptance of the updated policy.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our privacy practices,
              please contact us at:
            </p>
            <p className="privacy-contact">
              <strong>Email:</strong> <a href="mailto:info@thetempestradio.com">info@thetempestradio.com</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
